import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Environment } from '@/common/environment';
import { Config } from '@/common/config';
import Axios, { AxiosResponse } from 'axios';
import Shared from '@/common/shared';

// tslint:disable max-classes-per-file

export type RoleCode = 'Kiosk' | 'OrderPoint' | 'Unauthorised';
export class UserModel {
    public credentialIdentifier: string = '';
    public retailerId: number = -1;
    public storeId: number = -1;
    public deviceId: number = -1;
    public deviceName: string = '';
    public roleCode: RoleCode = 'Unauthorised';
    public token: string;
    public exp: Date = new Date();
    constructor(token: string) {
        this.token = token;
    }
}

@Module({ name: 'StoreVuexModule' })
export default class StoreVuexModule extends VuexModule {
    private environment: Environment = new Environment();
    private config: Config = new Config();
    private userModel: UserModel = new UserModel('');
    private requiredRole: RoleCode | null = null;
    private internalApiKey: string = '';
    private internalParentOrigin: string | null = null;
    private internalCompany: string | null = null;
    private progressFlag: boolean = false;
    private backFlag: boolean = false;
    private noManualLookupFlag: boolean = false;
    private noProductRedirectFlag: boolean = false;

    @Mutation
    public setEnvironment(environment: Environment) {
        this.environment = environment;
    }

    @Mutation
    public setConfig(config: Config) {
        this.config = config;
    }

    @Mutation
    public setUserModel(user: UserModel) {
        this.userModel = user;
    }

    @Mutation
    public setApiKey(apiKey: string) {
        this.internalApiKey = apiKey;
    }

    @Mutation
    public setRequiredUserRole(roleCode: RoleCode | null) {
        this.requiredRole = roleCode;
    }

    @Mutation
    public setParentOrigin(origin: string | null) {
        this.internalParentOrigin = origin;
    }

    @Mutation
    public setCompany(company: string | null) {
        this.internalCompany = company;
    }

    @Mutation
    public setProgressFlag(processFlag: boolean) {
        this.progressFlag = processFlag;
    }

    @Mutation
    public setNoBack(noBack: boolean) {
        this.backFlag = noBack;
    }

    @Mutation
    public setNoManualLookup(noManualLookup: boolean) {
        this.noManualLookupFlag = noManualLookup;
    }

    @Mutation
    public setNoProductRedirect(noProductRedirect: boolean) {
        this.noProductRedirectFlag = noProductRedirect;
    }

    @Action({ commit: 'setUserModel' })
    public updateUserModel(token: string | null) {
        let user: UserModel = new UserModel('');
        if (token) {
            const jsonObj = JSON.parse(atob(token.split('.')[1]));
            if ('exp' in jsonObj) {
                const d = new Date(0);
                d.setUTCSeconds(jsonObj.exp);
                if (d >= new Date()) {
                    user = {
                        credentialIdentifier: jsonObj.credentialIdentifier,
                        exp: d,
                        retailerId: jsonObj.retailerId,
                        roleCode: jsonObj.roleCode,
                        token,
                        storeId: -1,
                        deviceId: -1,
                        deviceName: '',
                    };

                    // if (sessionStorage && sessionStorage.setItem) {
                    //     sessionStorage.setItem(
                    //         Shared.localStorageKeys.API_BEARER_TOKEN,
                    //         token,
                    //     );
                    // }
                }
            }
        }

        return user;
    }

    @Action({ commit: 'setApiKey' })
    public storeApiKey(): string {
        const search = location.search.replace('?', '');
        const searchParts = search.split('&');
        const apiPart = searchParts.find((p) => p.includes('apiKey'));
        if (apiPart) {
            return apiPart.substr('apiKey='.length);
        } else {
            return '';
        }
    }

    @Action({ commit: 'setParentOrigin' })
    public storeParentOrigin(): string {
        const search = location.search;
        const searchParts = search.split('&');
        const originPart = searchParts.find((p) => p.includes('origin'));
        if (originPart) {
            return originPart.substr('origin='.length).replaceAll('%3A', ':').replaceAll('%2F', '/');
        } else {
            return '';
        }
    }

    @Action({ commit: 'setCompany' })
    public storeCompany(): string {
        const search = location.search;
        const searchParts = search.split('&');
        const companyPart = searchParts.find((p) => p.includes('company'));
        if (companyPart) {
            return companyPart.substr('company='.length)
                .replaceAll('%3A', ':')
                .replaceAll('%2F', '/')
                .replaceAll('%20', ' ');
        } else {
            return '';
        }
    }

    @Action({ commit: 'setProgressFlag' })
    public storeNoProgress(): boolean {
        const search = location.search;
        const searchParts = search.split('&');
        const noProgressPart = searchParts.find((p) => p.includes('noProgress'));
        if (noProgressPart) {
            return noProgressPart.substr('noProgress='.length).replaceAll('%3A', ':').replaceAll('%2F', '/') === 'true';
        } else {
            return false;
        }
    }

    @Action({ commit: 'setNoBack' })
    public storeNoBack(): boolean {
        const search = location.search;
        const searchParts = search.split('&');
        const noBackPart = searchParts.find((p) => p.includes('noBack'));
        if (noBackPart) {
            return noBackPart.substr('noBack='.length).replaceAll('%3A', ':').replaceAll('%2F', '/') === 'true';
        } else {
            return false;
        }
    }

    @Action({ commit: 'setNoManualLookup' })
    public storeNoManualLookup(): boolean {
        const search = location.search;
        const searchParts = search.split('&');
        const noManualLookupPart = searchParts.find((p) => p.includes('noManualLookup'));
        if (noManualLookupPart) {
            // tslint:disable-next-line: max-line-length
            return noManualLookupPart.substr('noManualLookup='.length).replaceAll('%3A', ':').replaceAll('%2F', '/') === 'true';
        } else {
            return false;
        }
    }

    @Action({ commit: 'setNoProductRedirect' })
    public storeNoProductRedirect(): boolean {
        const search = location.search;
        const searchParts = search.split('&');
        const noProductRedirectPart = searchParts.find((p) => p.includes('noProductRedirect'));
        if (noProductRedirectPart) {
            // tslint:disable-next-line: max-line-length
            return noProductRedirectPart.substr('noProductRedirect='.length).replaceAll('%3A', ':').replaceAll('%2F', '/') === 'true';
        } else {
            return false;
        }
    }

    @Action
    public async loadStoreFromSettings(): Promise<[boolean, string]> {
        let res: AxiosResponse<Config>;
        try {
            res = await Axios.get<Config>('/config.json', {
                validateStatus: () => true,
            });
        } catch (err) {
            // tslint:disable-next-line: no-console
            console.log(err);
            return [false, 'failed to load config.json'];
        }

        if (res.status !== 200 || !res.data) {
            return [false, `failed to load config.json (${res.status})`];
        }

        const environment = Object.assign(new Environment(), {
            VGroupApiBaseUrl: process.env.VUE_APP_API_BASE_URL,
            BlobStorageUrl: process.env.VUE_APP_BLOB_STORAGE_URL,
            AttributeImageContainer: process.env.VUE_APP_ATTRIBUTE_IMAGE_CONTAINER,
            SignalREndpoint: process.env.VUE_APP_SIGNAL_R_ENDPOINT,
            RecaptchaSiteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY,
        } as Environment);
        this.context.commit(
            'setEnvironment',
            environment,
        );
        this.context.commit('setConfig', Object.assign(new Config(), res.data));
        // if (sessionStorage && sessionStorage.getItem) {
        //     const token = sessionStorage.getItem(
        //         Shared.localStorageKeys.API_BEARER_TOKEN,
        //     );
        //     await this.updateUserModel(token);
        // }
        return [true, ''];
    }

    public get baseApiUrl(): string {
        return this.environment.VGroupApiBaseUrl;
    }

    public get env(): Environment {
        return this.environment;
    }

    public get attributeImageUrl(): string {
        return this.environment.AttributeImageUrl;
    }

    public get currentConfig(): Config {
        return this.config;
    }

    public get user(): UserModel {
        return this.userModel;
    }

    public get requiredUserRole(): RoleCode | null {
        return this.requiredRole;
    }

    public get hasRequiredUserRole(): boolean {
        return this.requiredUserRole === null
            ? true
            : this.user && this.user.roleCode === this.requiredUserRole;
    }

    public get apiKey(): string {
        return this.internalApiKey;
    }

    public get parentOrigin(): string | null {
        return this.internalParentOrigin;
    }

    public get company(): string | null {
        return this.internalCompany;
    }

    public get noProgress(): boolean {
        return this.progressFlag;
    }

    public get noBack(): boolean {
        return this.backFlag;
    }

    public get noManualLookup(): boolean {
        return this.noManualLookupFlag;
    }

    public get noProductRedirect(): boolean {
        return this.noProductRedirectFlag;
    }
}
